import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ArrowLeft,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Award,
  Target,
  Search,
  BookOpen,
  Shield,
  TrendingUp,
  FileText,
  Eye,
  BarChart3,
  Download,
  Filter,
  Play,
  Loader2,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import {
  evalResultsApi,
  ParsedEvalResult,
  RunSummary,
  RunInfo,
  EvalRunStatusInfo,
} from '../../services/evalResultsApi';

// ─── Utility Components ──────────────────────────────────────────────────────

function GradeBadge({ grade, size = 'lg' }: { grade: string; size?: 'sm' | 'lg' }) {
  const isA = grade.startsWith('A');
  const isB = grade.startsWith('B');
  const bg = isA ? 'bg-green-100 text-green-800 border-green-300'
    : isB ? 'bg-blue-100 text-blue-800 border-blue-300'
    : 'bg-amber-100 text-amber-800 border-amber-300';

  const sizing = size === 'lg' ? 'text-3xl px-5 py-2' : 'text-sm px-2 py-0.5';

  return (
    <span className={`inline-flex items-center font-bold rounded-lg border-2 ${bg} ${sizing}`}>
      {grade}
    </span>
  );
}

function MetricBar({ value, max = 1, color, label, showPct = true }: {
  value: number;
  max?: number;
  color: string;
  label: string;
  showPct?: boolean;
}) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600">{label}</span>
        {showPct && <span className="font-medium text-gray-900">{pct.toFixed(1)}%</span>}
      </div>
      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function DistributionBar({ counts, total, labels, colors }: {
  counts: number[];
  total: number;
  labels: string[];
  colors: string[];
}) {
  if (total === 0) return null;
  return (
    <div className="space-y-2">
      <div className="flex h-6 rounded-full overflow-hidden bg-gray-100">
        {counts.map((count, i) => {
          const pct = (count / total) * 100;
          if (pct === 0) return null;
          return (
            <div
              key={labels[i]}
              className="h-full transition-all duration-700 flex items-center justify-center text-xs font-medium text-white"
              style={{ width: `${pct}%`, backgroundColor: colors[i], minWidth: pct > 0 ? '20px' : 0 }}
              title={`${labels[i]}: ${count} (${pct.toFixed(1)}%)`}
            >
              {pct >= 8 ? count : ''}
            </div>
          );
        })}
      </div>
      <div className="flex gap-4 flex-wrap">
        {labels.map((label, i) => (
          <div key={label} className="flex items-center gap-1.5 text-xs text-gray-600">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors[i] }} />
            {label}: {counts[i]} ({total > 0 ? ((counts[i] / total) * 100).toFixed(0) : 0}%)
          </div>
        ))}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, subValue, color }: {
  icon: typeof Award;
  label: string;
  value: string;
  subValue?: string;
  color: string;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        <div className={`p-2.5 rounded-lg ${color}`}>
          <Icon size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
          {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
        </div>
      </div>
    </div>
  );
}

// ─── Question Detail Modal ───────────────────────────────────────────────────

function QuestionDetailModal({ result, onClose }: {
  result: ParsedEvalResult;
  onClose: () => void;
}) {
  const scorePct = (result.compositeScore * 100).toFixed(1);
  const grade = evalResultsApi.getLetterGrade(result.compositeScore);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <span className="text-sm font-mono text-gray-400">{result.question_id}</span>
            <GradeBadge grade={grade} size="sm" />
            <span className="text-sm text-gray-500">{scorePct}%</span>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-400">
            <ChevronDown size={20} />
          </button>
        </div>

        {/* Question */}
        <div className="px-6 py-4 bg-gray-50">
          <p className="text-sm font-medium text-gray-500 mb-1">Question</p>
          <p className="text-gray-900 font-medium">{result.question_text}</p>
        </div>

        {/* Side-by-side answers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-100">
          <div className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen size={16} className="text-gray-400" />
              <p className="text-sm font-semibold text-gray-700">Reference Answer</p>
            </div>
            <div className="text-sm text-gray-600 whitespace-pre-wrap max-h-64 overflow-y-auto bg-gray-50 rounded-lg p-3 leading-relaxed">
              {result.reference_answer}
            </div>
          </div>
          <div className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <Target size={16} className="text-indigo-500" />
              <p className="text-sm font-semibold text-indigo-700">Assistant Answer</p>
            </div>
            <div className="text-sm text-gray-600 whitespace-pre-wrap max-h-64 overflow-y-auto bg-indigo-50 rounded-lg p-3 leading-relaxed">
              {result.assistant_answer}
            </div>
          </div>
        </div>

        {/* Metric Bars */}
        <div className="px-6 py-5 border-t border-gray-100 space-y-3">
          <p className="text-sm font-semibold text-gray-700 mb-3">Score Breakdown</p>
          <MetricBar value={result.numSimilarity} color="#6366f1" label="Semantic Similarity (40% weight)" />
          <MetricBar value={result.numKeywordOverlap} color="#8b5cf6" label="Keyword Overlap (20% weight)" />
          <MetricBar value={result.avgSourceRelevance} color="#3b82f6" label="Source Relevance (20% weight)" />
          <MetricBar value={result.numConfidence} color="#06b6d4" label="API Confidence (20% weight)" />
        </div>

        {/* Haiku Judge */}
        {(result.judge_score !== null || result.retrieval_hit !== null) && (
          <div className="px-6 py-4 border-t border-gray-100">
            <p className="text-sm font-semibold text-gray-700 mb-2">LLM Judge</p>
            <div className="flex flex-wrap items-center gap-3 mb-2 text-xs">
              {result.judge_score !== null && (
                <span className={`px-2 py-1 rounded-lg font-semibold ${
                  result.judge_score >= 70 ? 'bg-green-100 text-green-700'
                    : result.judge_score >= 40 ? 'bg-amber-100 text-amber-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  Judge score: {Math.round(result.judge_score)}/100
                </span>
              )}
              {result.retrieval_hit !== null && (
                <span className={`px-2 py-1 rounded-lg font-semibold ${
                  result.retrieval_hit ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {result.retrieval_hit
                    ? `Expected doc retrieved${result.expected_doc_rank ? ` (rank ${result.expected_doc_rank})` : ''}`
                    : 'Expected doc NOT retrieved'}
                </span>
              )}
              {result.judge_model && (
                <span className="text-gray-400">{result.judge_model}</span>
              )}
            </div>
            {result.judge_verdict && (
              <p className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2.5 leading-relaxed">
                {result.judge_verdict}
              </p>
            )}
          </div>
        )}

        {/* Sources */}
        {result.parsedSources.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-100">
            <p className="text-sm font-semibold text-gray-700 mb-2">Sources Cited ({result.numHasSources})</p>
            <div className="space-y-2">
              {result.parsedSources.map((src, i) => (
                <div key={i} className="flex items-start gap-2 text-xs bg-gray-50 rounded-lg p-2.5">
                  <FileText size={14} className="text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-700 truncate">{src.section || src.title}</p>
                    <p className="text-gray-400 truncate">{src.path}</p>
                  </div>
                  <span className="text-gray-500 flex-shrink-0">
                    {(src.relevance_score * 100).toFixed(0)}% relevance
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer Metadata */}
        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex flex-wrap gap-4 text-xs text-gray-400">
          <span>Confidence: {result.api_confidence_level}</span>
          <span>Length Ratio: {(result.numLengthRatio * 100).toFixed(0)}%</span>
          <span>Run: {result.run_id}</span>
          <span>Created: {result.created_at ? new Date(result.created_at).toLocaleString() : 'N/A'}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Main Dashboard ──────────────────────────────────────────────────────────

export function AssistantEvalDashboard() {
  const [results, setResults] = useState<ParsedEvalResult[]>([]);
  const [totalDbRows, setTotalDbRows] = useState(0);
  const [runs, setRuns] = useState<RunInfo[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string>('__all__');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedResult, setSelectedResult] = useState<ParsedEvalResult | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<'compositeScore' | 'numSimilarity' | 'numKeywordOverlap' | 'numConfidence' | 'question_id'>('question_id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(0);
  const [confidenceFilter, setConfidenceFilter] = useState<string>('all');
  const [scoreFilter, setScoreFilter] = useState<string>('all');
  const pageSize = 25;

  // Run controls state
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [startIdx, setStartIdx] = useState(0);
  const [endIdx, setEndIdx] = useState(50);
  const [activeRun, setActiveRun] = useState<EvalRunStatusInfo | null>(null);
  const [startingRun, setStartingRun] = useState(false);
  const [showRunPanel, setShowRunPanel] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Load available runs on mount
  useEffect(() => {
    evalResultsApi.getRuns()
      .then(r => setRuns(r))
      .catch(() => { /* runs endpoint failed, will show error on results load */ });
  }, []);

  // Load results when selectedRunId changes (defaults to '__all__')
  const loadResults = useCallback(async () => {
    if (!selectedRunId) return;
    setLoading(true);
    setError(null);
    try {
      const runIdParam = selectedRunId === '__all__' ? undefined : selectedRunId;
      const { results: data, total } = await evalResultsApi.getResults(runIdParam);
      setResults(data);
      setTotalDbRows(total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load results from database');
      setResults([]);
      setTotalDbRows(0);
    } finally {
      setLoading(false);
    }
  }, [selectedRunId]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  // Load question count and check for active run on mount
  useEffect(() => {
    evalResultsApi.getQuestionCount()
      .then(({ total }) => {
        setTotalQuestions(total);
        setEndIdx(Math.min(50, total));
      })
      .catch(() => {});
    evalResultsApi.getActiveRun()
      .then(run => { if (run) { setActiveRun(run); setShowRunPanel(true); } })
      .catch(() => {});
  }, []);

  // Poll active run status every 5 seconds
  useEffect(() => {
    if (!activeRun || activeRun.status === 'completed' || activeRun.status === 'failed') return;

    const interval = setInterval(async () => {
      try {
        const status = await evalResultsApi.getRunStatus(activeRun.run_id);
        setActiveRun(status);
        if (status.status === 'completed' || status.status === 'failed') {
          // Refresh runs list and reload current view
          evalResultsApi.getRuns().then(r => setRuns(r));
          loadResults();
        }
      } catch { /* ignore poll errors */ }
    }, 5000);

    return () => clearInterval(interval);
  }, [activeRun, loadResults]);

  const handleStartRun = async () => {
    setStartingRun(true);
    try {
      const resp = await evalResultsApi.startRun({
        start_index: startIdx,
        end_index: endIdx,
      });
      setActiveRun({
        run_id: resp.run_id,
        status: 'queued',
        total: resp.total,
        completed: 0,
        failed: 0,
        started_at: new Date().toISOString(),
        finished_at: null,
        error_message: null,
      });
      setShowRunPanel(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start evaluation run');
    } finally {
      setStartingRun(false);
    }
  };

  // Derived summary (memoized for large datasets)
  const summary: RunSummary = useMemo(() => {
    return evalResultsApi.getSummary(results);
  }, [results]);

  // Filtered + sorted results (memoized)
  const filteredResults = useMemo(() => {
    let filtered = results;

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.question_text.toLowerCase().includes(q) ||
        r.question_id.toLowerCase().includes(q)
      );
    }

    // Confidence filter
    if (confidenceFilter !== 'all') {
      filtered = filtered.filter(r => r.api_confidence_level === confidenceFilter);
    }

    // Score filter
    if (scoreFilter !== 'all') {
      filtered = filtered.filter(r => {
        if (scoreFilter === 'excellent') return r.compositeScore >= 0.85;
        if (scoreFilter === 'good') return r.compositeScore >= 0.70 && r.compositeScore < 0.85;
        if (scoreFilter === 'fair') return r.compositeScore >= 0.50 && r.compositeScore < 0.70;
        if (scoreFilter === 'poor') return r.compositeScore < 0.50;
        return true;
      });
    }

    // Sort
    filtered = [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'question_id') {
        cmp = a.question_id.localeCompare(b.question_id, undefined, { numeric: true });
      } else {
        cmp = (a[sortField] as number) - (b[sortField] as number);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return filtered;
  }, [results, searchQuery, confidenceFilter, scoreFilter, sortField, sortDir]);

  // Paginated results
  const totalPages = Math.ceil(filteredResults.length / pageSize);
  const paginatedResults = useMemo(() => {
    const start = currentPage * pageSize;
    return filteredResults.slice(start, start + pageSize);
  }, [filteredResults, currentPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [searchQuery, confidenceFilter, scoreFilter]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'question_id' ? 'asc' : 'desc');
    }
  };

  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) return <ChevronDown size={14} className="text-gray-300" />;
    return sortDir === 'asc'
      ? <ChevronUp size={14} className="text-indigo-500" />
      : <ChevronDown size={14} className="text-indigo-500" />;
  };

  const handleExportCSV = () => {
    const headers = ['Question ID', 'Question', 'Composite Score', 'Similarity', 'Keyword Overlap', 'Confidence', 'Source Relevance', 'Sources', 'Grade'];
    const rows = filteredResults.map(r => [
      r.question_id,
      `"${r.question_text.replace(/"/g, '""')}"`,
      (r.compositeScore * 100).toFixed(1),
      (r.numSimilarity * 100).toFixed(1),
      (r.numKeywordOverlap * 100).toFixed(1),
      (r.numConfidence * 100).toFixed(1),
      (r.avgSourceRelevance * 100).toFixed(1),
      r.numHasSources,
      evalResultsApi.getLetterGrade(r.compositeScore),
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `eval-results-${summary.run_id || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.85) return 'text-green-600';
    if (score >= 0.70) return 'text-blue-600';
    if (score >= 0.50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBg = (score: number) => {
    if (score >= 0.85) return 'bg-green-50';
    if (score >= 0.70) return 'bg-blue-50';
    if (score >= 0.50) return 'bg-amber-50';
    return 'bg-red-50';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw size={40} className="mx-auto text-indigo-400 animate-spin" />
          <p className="text-gray-500 mt-4">Loading evaluation results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Nav */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="#/" className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500">
              <ArrowLeft size={20} />
            </a>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Assistant Evaluation Report</h1>
              <p className="text-xs text-gray-400">
                Source: assistant_eval_results &middot; {totalDbRows} rows in DB &middot; {summary.total_questions} loaded
                {selectedRunId !== '__all__' && <> &middot; Run: {selectedRunId}</>}
              </p>
            </div>
            {/* Run selector */}
            <select
              value={selectedRunId}
              onChange={(e) => setSelectedRunId(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500 bg-white"
            >
              <option value="__all__">All Runs ({totalDbRows} questions)</option>
              {runs.map(r => (
                <option key={r.run_id} value={r.run_id}>
                  Run {r.run_id} ({r.total_questions} questions)
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                if (generating) return;
                setGenerating(true);
                try {
                  const r = await evalResultsApi.generateQuestions({ doc_count: 5, per_doc: 2 });
                  alert(
                    `Generated ${r.questions_created} questions from ${r.sampled_docs} random documents.\n${r.note}`
                  );
                } catch (e) {
                  alert(`Generation failed: ${e instanceof Error ? e.message : e}`);
                } finally {
                  setGenerating(false);
                }
              }}
              disabled={generating}
              title="Haiku frames fresh questions from random documents; they arrive unapproved for review"
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200 disabled:opacity-50"
            >
              <RefreshCw size={15} className={generating ? 'animate-spin' : ''} />
              {generating ? 'Generating…' : 'Generate Questions'}
            </button>
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
            >
              <Download size={15} />
              Export CSV
            </button>
            <button
              onClick={loadResults}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
            >
              <RefreshCw size={15} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
            {error}
          </div>
        )}

        {/* ── Run Evaluation Panel ─────────────────────────────────────── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <button
            onClick={() => setShowRunPanel(p => !p)}
            className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Play size={18} className="text-indigo-500" />
              <span className="text-sm font-semibold text-gray-800">Run New Evaluation</span>
              {totalQuestions > 0 && (
                <span className="text-xs text-gray-400">({totalQuestions} questions available)</span>
              )}
            </div>
            <ChevronDown size={16} className={`text-gray-400 transition-transform ${showRunPanel ? 'rotate-180' : ''}`} />
          </button>

          {showRunPanel && (
            <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
              {/* Active run progress */}
              {activeRun && (activeRun.status === 'queued' || activeRun.status === 'running') && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Loader2 size={16} className="text-indigo-600 animate-spin" />
                    <span className="text-sm font-medium text-indigo-800">
                      Run {activeRun.run_id} — {activeRun.status === 'queued' ? 'Starting...' : 'Running'}
                    </span>
                    <span className="text-xs text-indigo-500 ml-auto">
                      {activeRun.completed + activeRun.failed} / {activeRun.total}
                    </span>
                  </div>
                  <div className="h-3 bg-indigo-100 rounded-full overflow-hidden">
                    <div className="h-full flex">
                      <div
                        className="bg-indigo-500 transition-all duration-500"
                        style={{ width: activeRun.total > 0 ? `${(activeRun.completed / activeRun.total) * 100}%` : '0%' }}
                      />
                      {activeRun.failed > 0 && (
                        <div
                          className="bg-red-400 transition-all duration-500"
                          style={{ width: `${(activeRun.failed / activeRun.total) * 100}%` }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex gap-4 mt-2 text-xs">
                    <span className="text-green-600">{activeRun.completed} completed</span>
                    {activeRun.failed > 0 && <span className="text-red-600">{activeRun.failed} failed</span>}
                    <span className="text-gray-400">{activeRun.total - activeRun.completed - activeRun.failed} remaining</span>
                  </div>
                </div>
              )}

              {/* Completed/Failed run result */}
              {activeRun && activeRun.status === 'completed' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-green-600" />
                  <span className="text-sm text-green-800">
                    Run {activeRun.run_id} completed: {activeRun.completed} succeeded, {activeRun.failed} failed
                  </span>
                  <button onClick={() => setActiveRun(null)} className="ml-auto text-xs text-green-600 hover:underline">Dismiss</button>
                </div>
              )}
              {activeRun && activeRun.status === 'failed' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <AlertTriangle size={16} className="text-red-600" />
                  <span className="text-sm text-red-800">
                    Run {activeRun.run_id} failed: {activeRun.error_message || 'Unknown error'}
                  </span>
                  <button onClick={() => setActiveRun(null)} className="ml-auto text-xs text-red-600 hover:underline">Dismiss</button>
                </div>
              )}

              {/* Start new run controls */}
              {(!activeRun || activeRun.status === 'completed' || activeRun.status === 'failed') && (
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">From Question</label>
                    <input
                      type="number"
                      min={0}
                      max={totalQuestions}
                      value={startIdx}
                      onChange={(e) => setStartIdx(Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-28 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">To Question</label>
                    <input
                      type="number"
                      min={startIdx + 1}
                      max={totalQuestions}
                      value={endIdx}
                      onChange={(e) => setEndIdx(Math.max(startIdx + 1, parseInt(e.target.value) || 0))}
                      className="w-28 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="text-xs text-gray-400 pb-2">
                    {Math.max(0, endIdx - startIdx)} questions selected
                    {totalQuestions > 0 && ` of ${totalQuestions}`}
                  </div>
                  <div className="flex-1" />
                  <button
                    onClick={handleStartRun}
                    disabled={startingRun || endIdx <= startIdx}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    {startingRun ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                    Start Evaluation
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Hero Score Section ────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 via-indigo-500 to-purple-600 px-8 py-10">
            <div className="flex flex-col lg:flex-row items-center gap-8">
              {/* Score Ring */}
              <div className="flex flex-col items-center">
                <div className="relative" style={{ width: 180, height: 180 }}>
                  <svg width={180} height={180} className="transform -rotate-90">
                    <circle cx={90} cy={90} r={78} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth={14} />
                    <circle
                      cx={90} cy={90} r={78}
                      fill="none" stroke="white" strokeWidth={14}
                      strokeDasharray={78 * 2 * Math.PI}
                      strokeDashoffset={78 * 2 * Math.PI * (1 - summary.composite_score)}
                      strokeLinecap="round"
                      className="transition-all duration-1000 ease-out"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-5xl font-extrabold text-white">{Math.round(summary.composite_score * 100)}</span>
                    <span className="text-xs text-indigo-200 uppercase tracking-widest mt-1">Overall Score</span>
                  </div>
                </div>
              </div>

              {/* Grade + Summary */}
              <div className="flex-1 text-center lg:text-left">
                <div className="flex items-center gap-4 justify-center lg:justify-start mb-4">
                  <span className="text-6xl font-black text-white tracking-tight">{summary.letter_grade}</span>
                  <div className="text-indigo-100">
                    <p className="text-lg font-semibold">
                      {summary.composite_score >= 0.85 ? 'Excellent Performance' :
                       summary.composite_score >= 0.70 ? 'Good Performance' :
                       summary.composite_score >= 0.50 ? 'Fair Performance' : 'Needs Improvement'}
                    </p>
                    <p className="text-sm text-indigo-200 mt-0.5">
                      Based on {summary.total_questions} evaluated questions
                    </p>
                  </div>
                </div>

                {/* Quick Metric Bars */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                  {[
                    { label: 'Similarity', value: summary.avg_similarity, desc: 'Semantic match' },
                    { label: 'Keywords', value: summary.avg_keyword_overlap, desc: 'Term coverage' },
                    { label: 'Sources', value: summary.avg_source_relevance, desc: 'Retrieval quality' },
                    { label: 'Confidence', value: summary.avg_confidence, desc: 'Model certainty' },
                  ].map(m => (
                    <div key={m.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                      <div className="flex justify-between items-baseline">
                        <span className="text-xs text-indigo-200">{m.label}</span>
                        <span className="text-lg font-bold text-white">{(m.value * 100).toFixed(0)}%</span>
                      </div>
                      <div className="h-1.5 bg-white/20 rounded-full mt-2">
                        <div
                          className="h-full bg-white rounded-full transition-all duration-700"
                          style={{ width: `${m.value * 100}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-indigo-300 mt-1 block">{m.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Stat Cards ───────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={Target}
            label="Avg Semantic Similarity"
            value={`${(summary.avg_similarity * 100).toFixed(1)}%`}
            subValue="Meaning match vs reference"
            color="bg-indigo-500"
          />
          <StatCard
            icon={Search}
            label="Avg Keyword Overlap"
            value={`${(summary.avg_keyword_overlap * 100).toFixed(1)}%`}
            subValue="Key term coverage"
            color="bg-purple-500"
          />
          <StatCard
            icon={BookOpen}
            label="Avg Source Relevance"
            value={`${(summary.avg_source_relevance * 100).toFixed(1)}%`}
            subValue="Retrieved document quality"
            color="bg-blue-500"
          />
          <StatCard
            icon={Shield}
            label="Avg Confidence"
            value={`${(summary.avg_confidence * 100).toFixed(1)}%`}
            subValue="Model self-assessment"
            color="bg-cyan-500"
          />
        </div>

        {/* ── Distributions ────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={18} className="text-indigo-500" />
              <h3 className="text-sm font-semibold text-gray-800">Score Distribution</h3>
            </div>
            <DistributionBar
              counts={[
                summary.score_distribution.excellent,
                summary.score_distribution.good,
                summary.score_distribution.fair,
                summary.score_distribution.poor,
              ]}
              total={summary.total_questions}
              labels={['Excellent (85%+)', 'Good (70-84%)', 'Fair (50-69%)', 'Poor (<50%)']}
              colors={['#22c55e', '#3b82f6', '#f59e0b', '#ef4444']}
            />
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={18} className="text-indigo-500" />
              <h3 className="text-sm font-semibold text-gray-800">Confidence Distribution</h3>
            </div>
            <DistributionBar
              counts={[
                summary.confidence_distribution.high,
                summary.confidence_distribution.medium,
                summary.confidence_distribution.low,
              ]}
              total={summary.total_questions}
              labels={['High', 'Medium', 'Low']}
              colors={['#22c55e', '#f59e0b', '#ef4444']}
            />
          </div>
        </div>

        {/* ── Score Weight Explanation ─────────────────────────────────── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Award size={18} className="text-indigo-500" />
            <h3 className="text-sm font-semibold text-gray-800">Composite Score Methodology</h3>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Semantic Similarity', weight: '40%', desc: 'Measures how closely the assistant\'s answer matches the reference answer in meaning using vector embeddings.', color: 'bg-indigo-100 text-indigo-700' },
              { label: 'Keyword Overlap', weight: '20%', desc: 'Calculates the overlap of key terms between reference and assistant answers for factual accuracy.', color: 'bg-purple-100 text-purple-700' },
              { label: 'Source Quality', weight: '20%', desc: 'Evaluates whether relevant source documents were retrieved and their relevance scores.', color: 'bg-blue-100 text-blue-700' },
              { label: 'Confidence', weight: '20%', desc: 'The model\'s self-assessed confidence in its answer, indicating response reliability.', color: 'bg-cyan-100 text-cyan-700' },
            ].map(w => (
              <div key={w.label} className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${w.color}`}>{w.weight}</span>
                  <span className="text-sm font-medium text-gray-800">{w.label}</span>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Question-Level Results Table ─────────────────────────────── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Table Header / Filters */}
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex items-center gap-2">
                <FileText size={18} className="text-indigo-500" />
                <h3 className="text-sm font-semibold text-gray-800">
                  Question-Level Results
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    ({filteredResults.length} of {results.length})
                  </span>
                </h3>
              </div>

              <div className="flex-1" />

              <div className="flex flex-wrap items-center gap-2">
                {/* Search */}
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search questions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-48"
                  />
                </div>

                {/* Confidence filter */}
                <div className="flex items-center gap-1.5">
                  <Filter size={14} className="text-gray-400" />
                  <select
                    value={confidenceFilter}
                    onChange={(e) => setConfidenceFilter(e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="all">All Confidence</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>

                {/* Score band filter */}
                <select
                  value={scoreFilter}
                  onChange={(e) => setScoreFilter(e.target.value)}
                  className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">All Scores</option>
                  <option value="excellent">Excellent (85%+)</option>
                  <option value="good">Good (70-84%)</option>
                  <option value="fair">Fair (50-69%)</option>
                  <option value="poor">Poor (&lt;50%)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-indigo-600"
                    onClick={() => handleSort('question_id')}
                  >
                    <div className="flex items-center gap-1">ID <SortIcon field="question_id" /></div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Question
                  </th>
                  <th
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-indigo-600"
                    onClick={() => handleSort('compositeScore')}
                  >
                    <div className="flex items-center justify-center gap-1">Score <SortIcon field="compositeScore" /></div>
                  </th>
                  <th
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-indigo-600"
                    onClick={() => handleSort('numSimilarity')}
                  >
                    <div className="flex items-center justify-center gap-1">Similarity <SortIcon field="numSimilarity" /></div>
                  </th>
                  <th
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-indigo-600"
                    onClick={() => handleSort('numKeywordOverlap')}
                  >
                    <div className="flex items-center justify-center gap-1">Keywords <SortIcon field="numKeywordOverlap" /></div>
                  </th>
                  <th
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-indigo-600"
                    onClick={() => handleSort('numConfidence')}
                  >
                    <div className="flex items-center justify-center gap-1">Confidence <SortIcon field="numConfidence" /></div>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Sources
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Grade
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Detail
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedResults.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-gray-400">
                      No results match your filters.
                    </td>
                  </tr>
                ) : paginatedResults.map((r) => {
                  const grade = evalResultsApi.getLetterGrade(r.compositeScore);
                  return (
                    <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-xs font-mono text-gray-400">{r.question_id}</td>
                      <td className="px-4 py-3 text-sm text-gray-800 max-w-sm">
                        <p className="truncate" title={r.question_text}>{r.question_text}</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-sm font-bold ${getScoreColor(r.compositeScore)}`}>
                          {(r.compositeScore * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <div className="w-12 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${r.numSimilarity * 100}%` }} />
                          </div>
                          <span className="text-xs text-gray-500 w-10 text-right">{(r.numSimilarity * 100).toFixed(0)}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <div className="w-12 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-purple-500 rounded-full" style={{ width: `${r.numKeywordOverlap * 100}%` }} />
                          </div>
                          <span className="text-xs text-gray-500 w-10 text-right">{(r.numKeywordOverlap * 100).toFixed(0)}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          r.api_confidence_level === 'high' ? 'bg-green-100 text-green-700'
                          : r.api_confidence_level === 'medium' ? 'bg-amber-100 text-amber-700'
                          : 'bg-red-100 text-red-700'
                        }`}>
                          {(r.numConfidence * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-xs text-gray-500">
                        {r.numHasSources > 0 ? (
                          <span className="inline-flex items-center gap-1 text-blue-600">
                            <FileText size={12} />
                            {r.numHasSources}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center justify-center w-8 h-8 text-xs font-bold rounded-lg ${getScoreBg(r.compositeScore)} ${getScoreColor(r.compositeScore)}`}>
                          {grade}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setSelectedResult(r)}
                          className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="View detail"
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Showing {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, filteredResults.length)} of {filteredResults.length} results
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(0)}
                  disabled={currentPage === 0}
                  className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className="p-1 text-gray-600 hover:bg-gray-200 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="px-2 text-xs text-gray-600">
                  Page {currentPage + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className="p-1 text-gray-600 hover:bg-gray-200 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={16} />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages - 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Question Detail Modal */}
      {selectedResult && (
        <QuestionDetailModal
          result={selectedResult}
          onClose={() => setSelectedResult(null)}
        />
      )}
    </div>
  );
}
